import React, { useState, useEffect, useCallback } from 'react';
import { getBackendUrl } from '../utils/api';
import storage from '../utils/storage';

const BACKEND_URL = getBackendUrl();
const getToken = () => storage.getItem('token');

/**
 * Dashboard Overview Widget
 * Shows maintenance stats, system health, and recent activity
 */
const DashboardOverviewWidget = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [enrichmentRunning, setEnrichmentRunning] = useState(false);
  const [reclassStats, setReclassStats] = useState(null);
  const [reclassRunning, setReclassRunning] = useState(false);

  const fetchStats = useCallback(async () => {
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/maintenance-stats`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      
      if (!response.ok) throw new Error('Failed to fetch maintenance stats');
      
      const data = await response.json();
      setStats(data);
      setLastRefresh(new Date());
      setError(null);
      
      // Also fetch reclassification stats
      try {
        const reclassResponse = await fetch(`${BACKEND_URL}/api/admin/system/chart-reclassification/stats`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (reclassResponse.ok) {
          const reclassData = await reclassResponse.json();
          setReclassStats(reclassData);
          setReclassRunning(reclassData.job_status?.status === 'running');
        }
      } catch (reclassErr) {
        console.log('Reclassification stats not available');
      }
    } catch (err) {
      console.error('Error fetching stats:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  // Run enrichment manually
  const runEnrichment = async () => {
    setEnrichmentRunning(true);
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/run-enrichment-now`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`✅ Ξεκίνησε η ενημέρωση ετών για ${result.status?.total || 0} τραγούδια!`);
        // Refresh stats after a delay
        setTimeout(fetchStats, 5000);
      } else {
        alert(`❌ Σφάλμα: ${result.error || result.message}`);
      }
    } catch (err) {
      console.error('Run enrichment error:', err);
      alert(`❌ Σφάλμα: ${err.message}`);
    } finally {
      setEnrichmentRunning(false);
    }
  };

  // Run chart reclassification manually
  const runReclassification = async () => {
    setReclassRunning(true);
    try {
      const token = getToken();
      const response = await fetch(`${BACKEND_URL}/api/admin/system/chart-reclassification/start`, {
        method: 'POST',
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      if (result.success) {
        alert(`✅ Ξεκίνησε το Chart Reclassification για ${result.status?.total || 0} records!`);
        // Refresh stats periodically while running
        const pollInterval = setInterval(async () => {
          const statusRes = await fetch(`${BACKEND_URL}/api/admin/system/chart-reclassification/status`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          const statusData = await statusRes.json();
          if (statusData.status !== 'running') {
            clearInterval(pollInterval);
            setReclassRunning(false);
            fetchStats();
          }
        }, 5000);
      } else {
        alert(`❌ Σφάλμα: ${result.error || result.message}`);
        setReclassRunning(false);
      }
    } catch (err) {
      console.error('Run reclassification error:', err);
      alert(`❌ Σφάλμα: ${err.message}`);
      setReclassRunning(false);
    }
  };

  useEffect(() => {
    fetchStats();
    
    // Auto-refresh every 60 seconds
    const interval = setInterval(fetchStats, 60000);
    return () => clearInterval(interval);
  }, [fetchStats]);

  const formatTimeAgo = (isoString) => {
    if (!isoString) return 'Ποτέ';
    try {
      const date = new Date(isoString);
      const now = new Date();
      const diff = now - date;
      
      if (diff < 60000) return 'Μόλις τώρα';
      if (diff < 3600000) return `${Math.round(diff / 60000)} λεπτά πριν`;
      if (diff < 86400000) return `${Math.round(diff / 3600000)} ώρες πριν`;
      return `${Math.round(diff / 86400000)} μέρες πριν`;
    } catch {
      return isoString;
    }
  };

  if (loading) {
    return (
      <div className="bg-gradient-to-br from-purple-900/30 to-blue-900/30 rounded-xl p-6 border border-purple-500/20">
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-400"></div>
          <span className="ml-3 text-gray-400">Φόρτωση στατιστικών...</span>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-900/20 rounded-xl p-6 border border-red-500/30">
        <p className="text-red-400">❌ Σφάλμα: {error}</p>
        <button onClick={fetchStats} className="mt-2 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700">
          Επανάληψη
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <span className="text-2xl">📊</span>
          Maintenance Overview
        </h2>
        <div className="text-right">
          <p className="text-xs text-gray-500">
            Τελευταία ενημέρωση: {lastRefresh?.toLocaleTimeString('el-GR')}
          </p>
          <button 
            onClick={fetchStats}
            className="text-xs text-purple-400 hover:text-purple-300"
          >
            🔄 Ανανέωση
          </button>
        </div>
      </div>

      {/* Quick Stats Grid */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Songs Missing Year - Now with Run Button */}
        <div className="bg-gradient-to-br from-blue-900/40 to-blue-800/20 rounded-lg p-4 border border-blue-500/30">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="text-2xl">🎵</span>
              <span className="text-sm text-blue-300">Χωρίς Έτος</span>
            </div>
            {/* Run Enrichment Button */}
            <button
              onClick={runEnrichment}
              disabled={enrichmentRunning || (stats?.songs_missing_year || 0) === 0}
              className={`px-2 py-1 text-xs rounded transition-all ${
                enrichmentRunning 
                  ? 'bg-green-600 text-white animate-pulse cursor-wait'
                  : (stats?.songs_missing_year || 0) === 0
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-green-600 hover:bg-green-500 text-white cursor-pointer'
              }`}
              title="Εκκίνηση αναζήτησης ετών"
            >
              {enrichmentRunning ? '⏳' : '▶️'}
            </button>
          </div>
          <p className="text-3xl font-bold text-white">
            {stats?.songs_missing_year?.toLocaleString() || 0}
          </p>
          <p className="text-xs text-blue-400 mt-1">τραγούδια</p>
        </div>

        {/* Last Duplicate Cleanup */}
        <div className="bg-gradient-to-br from-orange-900/40 to-orange-800/20 rounded-lg p-4 border border-orange-500/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🧹</span>
            <span className="text-sm text-orange-300">Duplicates</span>
          </div>
          <p className="text-3xl font-bold text-white">
            {stats?.last_duplicate_cleanup?.duplicates_removed || 0}
          </p>
          <p className="text-xs text-orange-400 mt-1">
            {stats?.last_duplicate_cleanup 
              ? formatTimeAgo(stats.last_duplicate_cleanup.timestamp)
              : 'Δεν έτρεξε ακόμα'}
          </p>
        </div>

        {/* Last Jingle Cleanup */}
        <div className="bg-gradient-to-br from-pink-900/40 to-pink-800/20 rounded-lg p-4 border border-pink-500/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">🎤</span>
            <span className="text-sm text-pink-300">Jingles</span>
          </div>
          <p className="text-3xl font-bold text-white">
            {stats?.last_jingle_cleanup?.jingles_removed || 0}
          </p>
          <p className="text-xs text-pink-400 mt-1">
            {stats?.last_jingle_cleanup 
              ? formatTimeAgo(stats.last_jingle_cleanup.timestamp)
              : 'Δεν έτρεξε ακόμα'}
          </p>
        </div>

        {/* Last Enrichment */}
        <div className="bg-gradient-to-br from-green-900/40 to-green-800/20 rounded-lg p-4 border border-green-500/30">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-2xl">📚</span>
            <span className="text-sm text-green-300">Enrichment</span>
          </div>
          <p className="text-3xl font-bold text-white">
            {stats?.last_enrichment?.songs_updated || 0}
          </p>
          <p className="text-xs text-green-400 mt-1">
            {stats?.last_enrichment 
              ? formatTimeAgo(stats.last_enrichment.timestamp)
              : 'Δεν έτρεξε ακόμα'}
          </p>
        </div>
      </div>

      {/* Chart Reclassification Section */}
      {reclassStats && (
        <div className="bg-gradient-to-br from-purple-900/30 to-indigo-900/20 rounded-lg p-4 border border-purple-500/30">
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center gap-2">
              <span className="text-2xl">📊</span>
              <span className="text-lg font-semibold text-purple-200">Chart Reclassification</span>
            </div>
            <button
              onClick={runReclassification}
              disabled={reclassRunning || (reclassStats?.needs_reclassification || 0) === 0}
              className={`px-3 py-1.5 text-sm rounded transition-all flex items-center gap-2 ${
                reclassRunning 
                  ? 'bg-purple-600 text-white animate-pulse cursor-wait'
                  : (reclassStats?.needs_reclassification || 0) === 0
                    ? 'bg-gray-600 text-gray-400 cursor-not-allowed'
                    : 'bg-purple-600 hover:bg-purple-500 text-white cursor-pointer'
              }`}
            >
              {reclassRunning ? (
                <>⏳ {reclassStats?.job_status?.message || 'Τρέχει...'}</>
              ) : (
                <>▶️ Εκκίνηση</>
              )}
            </button>
          </div>
          
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-gray-800/50 rounded p-3">
              <p className="text-xs text-gray-400">Χρειάζονται</p>
              <p className="text-xl font-bold text-yellow-400">
                {reclassStats?.needs_reclassification?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-gray-800/50 rounded p-3">
              <p className="text-xs text-gray-400">Ταξινομημένα</p>
              <p className="text-xl font-bold text-green-400">
                {reclassStats?.already_classified?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-gray-800/50 rounded p-3">
              <p className="text-xs text-gray-400">Songs</p>
              <p className="text-xl font-bold text-blue-400">
                {reclassStats?.by_content_type?.song?.toLocaleString() || 0}
              </p>
            </div>
            <div className="bg-gray-800/50 rounded p-3">
              <p className="text-xs text-gray-400">Excluded</p>
              <p className="text-xl font-bold text-red-400">
                {reclassStats?.excluded_from_charts?.toLocaleString() || 0}
              </p>
            </div>
          </div>
          
          {reclassRunning && reclassStats?.job_status && (
            <div className="mt-3 bg-gray-800/50 rounded p-2">
              <p className="text-xs text-purple-300">{reclassStats.job_status.message}</p>
            </div>
          )}
        </div>
      )}

      {/* Two Column Layout */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Next Scheduled Jobs */}
        <div className="bg-gray-800/50 rounded-lg border border-gray-700">
          <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
            <span className="text-xl">⏱️</span>
            <h3 className="font-semibold text-white">Επόμενα Jobs</h3>
          </div>
          <div className="divide-y divide-gray-700/50">
            {stats?.next_scheduled_jobs?.length > 0 ? (
              stats.next_scheduled_jobs.map((job, idx) => (
                <div key={idx} className="px-4 py-2 flex items-center justify-between hover:bg-gray-700/30">
                  <span className="text-sm text-gray-300 truncate">{job.name}</span>
                  <span className={`text-xs px-2 py-1 rounded ${
                    job.minutes_until < 5 
                      ? 'bg-green-900/50 text-green-300' 
                      : job.minutes_until < 30 
                        ? 'bg-yellow-900/50 text-yellow-300'
                        : 'bg-gray-700 text-gray-400'
                  }`}>
                    {job.minutes_until < 1 
                      ? 'Τώρα!' 
                      : job.minutes_until < 60 
                        ? `${job.minutes_until} λεπτά`
                        : `${Math.round(job.minutes_until / 60)} ώρες`}
                  </span>
                </div>
              ))
            ) : (
              <div className="px-4 py-4 text-center text-gray-500">
                Δεν υπάρχουν προγραμματισμένα jobs
              </div>
            )}
          </div>
        </div>

        {/* Recent Activity */}
        <div className="bg-gray-800/50 rounded-lg border border-gray-700">
          <div className="px-4 py-3 border-b border-gray-700 flex items-center gap-2">
            <span className="text-xl">📜</span>
            <h3 className="font-semibold text-white">Πρόσφατη Δραστηριότητα</h3>
          </div>
          <div className="divide-y divide-gray-700/50 max-h-64 overflow-y-auto">
            {stats?.recent_activity?.length > 0 ? (
              stats.recent_activity.map((activity, idx) => (
                <div key={idx} className="px-4 py-2 flex items-center gap-3 hover:bg-gray-700/30">
                  <span className="text-xl">{activity.icon}</span>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-300 truncate">{activity.message}</p>
                    <p className="text-xs text-gray-500">{formatTimeAgo(activity.timestamp)}</p>
                  </div>
                </div>
              ))
            ) : (
              <div className="px-4 py-4 text-center text-gray-500">
                Δεν υπάρχει πρόσφατη δραστηριότητα
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardOverviewWidget;
